import {
  Color,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  type Side,
  type Texture,
} from 'three/webgpu';
import {
  Fn,
  dot,
  float,
  materialColor,
  materialReference,
  mix,
  nodeObject,
  oneMinus,
  pow,
  saturate,
  step,
  vec3,
  vec4,
} from 'three/tsl';

/**
 * Material archetype factory (rendering plan §1.1 — the UE "master material + material
 * instances" pattern under three r185's actual constraints). THE ONE RULE: any TSL node graph
 * is built exactly ONCE at module scope; every material instance is a stock node material that
 * shares those node objects and varies only plain properties + texture slots. This is what
 * makes 500 materials from one archetype compile ONE program:
 *
 * - `NodeMaterial.customProgramCacheKey()` hashes node instance ids — a fresh graph per
 *   material = unique cache key = a full NodeBuilder + WGSL codegen run per material, even when
 *   the emitted source is identical. (Verified headlessly in tests/materialFactory.test.ts.)
 * - Custom parameters go through `materialReference('ap*', type)` — a shared node that resolves
 *   against the DRAWN material at frame time (`state.material`), so one node object serves every
 *   instance. Everything in `NodeMaterialObserver.refreshUniforms` (color, roughness, metalness,
 *   every `*Map`, `emissiveIntensity`, `aoMapIntensity`, …) varies at zero shader cost.
 *
 * RULE TABLE (plan §1.1 — each entry is a silent program fork if violated):
 * 1. Non-zero-vs-zero IS a recompile: `RenderObject.getMaterialCacheKey()` collapses numeric
 *    props to '1'/'0' — `alphaTest: 0.01` vs `0` forks a program, `0.3` vs `0.9` does not. On
 *    tier 0 every Physical lobe (clearcoat/sheen/iridescence/anisotropy/transmission/dispersion)
 *    must be EXACTLY 0; tier-downs happen at load, never mid-frame.
 * 2. Sampler state (mag/min filter, wrap, mapping) is in the WebGPU pipeline cache key; texture
 *    SWAPS with identical sampler state do NOT recompile (uuid is not in the key). One sampler
 *    config per texture role per tier; set filters explicitly on both sides of any swap.
 * 3. No negative scale on meshes — it flips frontFace and forks a whole GPURenderPipeline. Bake
 *    mirroring into geometry in the bundler.
 * 4. ORM stays on TEXCOORD_0 and is ONE texture assigned to aoMap+roughnessMap+metalnessMap —
 *    the uuid-hash binding dedupe collapses it to a single GPU binding. GLTFLoader clones the
 *    Texture for `texCoord > 0`, silently breaking that dedupe.
 * 5. Watch `renderer.info.memory.programs` per tier after loads: tier 0 ≤ 24, 1 ≤ 48, 2 ≤ 96.
 *    A jump means someone forked a variant — this table is the debugging checklist.
 *
 * Archetypes in R1: surf.opaque / surf.alphaTest / surf.emissive / surf.unlit. Physical lobes
 * (clearcoat, transmission) are R2+, tier-gated per the plan's table. Bespoke TSL one-offs live
 * in ./bespoke/ (plan §1.3), never here.
 *
 * surf.maskedRGB (Axon body port) is the first archetype that is a PORT of a specific UE master
 * material rather than a generic surface. It lives here and not in ./bespoke/ for the reason the
 * factory exists at all: eight UE colourways share one graph and differ only in uniforms, which
 * is precisely the "master material + instances" case, and the face atlas will be the second
 * instance of the same idea. The moment there is only ever going to be ONE of something, it
 * belongs in ./bespoke/ instead.
 */

export type ArchetypeId =
  | 'surf.opaque'
  | 'surf.alphaTest'
  | 'surf.emissive'
  | 'surf.unlit'
  | 'surf.maskedRGB';

export const ARCHETYPES: readonly ArchetypeId[] = [
  'surf.opaque',
  'surf.alphaTest',
  'surf.emissive',
  'surf.unlit',
  'surf.maskedRGB',
];

/** Texture slots the factory wires. `orm` is ONE texture bound to ao+roughness+metalness. */
export interface SurfaceTextures {
  map?: Texture;
  normalMap?: Texture;
  orm?: Texture;
  emissiveMap?: Texture;
  /**
   * surf.maskedRGB only: the three-region mask whose R/G/B each select one tint. It rides
   * `material.apMaskMap` (a `materialReference('…','texture')`), NOT a stock slot, so the shared
   * module-scope graph still serves every instance.
   */
  mask?: Texture;
}

/**
 * A colour, either as an AUTHORED value (hex/CSS string — three converts it from sRGB) or as a
 * LINEAR working-space triple.
 *
 * The tuple form exists because ported material parameters are already linear: UE's
 * `Colour_G = (1, 0.0801, 0)` is a linear vector, and writing it as the hex `0xff1400` would send
 * it through `Color.setHex`'s sRGB→linear conversion a second time and darken it to (1, 0.006, 0).
 * A wrong-but-plausible orange is exactly the kind of error that survives review.
 */
export type ColourValue = number | string | readonly [number, number, number];

/**
 * surf.maskedRGB parameters — a PORT of UE's `M_MaskedRGB`, not a re-authoring of it.
 *
 * The whole reason this archetype exists rather than a baked texture: the UE project carries
 * EIGHT material instances of that master (`MI_Axon` … `MI_Axon7`) which differ in nothing but
 * these three colours and two scalars. Baking picks one and throws a free wardrobe away.
 */
export interface MaskedRgbParams {
  /** Tint selected by the mask's RED channel. */
  colourR?: ColourValue;
  /** Tint selected by the mask's GREEN channel. */
  colourG?: ColourValue;
  /** Tint selected by the mask's BLUE channel. */
  colourB?: ColourValue;
  /** Expands the near-white greyscale base into a usable detail range. 1 = identity. */
  brightness?: number;
  /** Gamma over that detail range. 1 = identity. */
  contrast?: number;
  /** 1 = the tinted colour untouched, 0 = fully grey. */
  saturation?: number;
}

export interface SurfaceParams {
  /**
   * Base colour. A hex/CSS value is decoded from sRGB; a `[r, g, b]` triple is written STRAIGHT
   * into working space — which is what a ported UE `Tint` vector already is. See `toColour`.
   */
  color?: ColourValue;
  /** Multiplier over the ORM map's G channel (glTF semantics) — default 1. */
  roughness?: number;
  /** Multiplier over the ORM map's B channel — default 1 when an ORM is bound, else 0. */
  metalness?: number;
  /** Emissive colour — hex/CSS (decoded from sRGB) or a LINEAR triple, like {@link SurfaceParams.color}. */
  emissive?: ColourValue;
  emissiveIntensity?: number;
  /** surf.alphaTest only. MUST stay non-zero once chosen — zero↔non-zero is a recompile. */
  alphaTest?: number;
  side?: Side;
  /**
   * Optional per-instance tint through the shared `ap*` node graph (plan §1.1 sample).
   * Zero shader cost across instances — the ONE `COLOR_NODE` object is shared, and the tint
   * value rides `material.apTint` via `materialReference` (docs/ai/tsl-patterns.ts).
   */
  tint?: number | string;
  /** surf.maskedRGB only — see {@link MaskedRgbParams}. */
  maskedRGB?: MaskedRgbParams;
  textures?: SurfaceTextures;
}

// ── Module-scope node graph (built ONCE — see the file-top rule) ──
// Custom params are namespaced `ap*` and resolve per-material at draw time.
const kTint = materialReference('apTint', 'color');
// The public .d.ts types materialReference's return too narrowly for the fluent mul overloads —
// runtime-verified (headless cache-key run + tests/materialFactory.test.ts).
//
// ⚠️ NO `.setLayout()`, AND THAT IS A FIX, NOT A SIMPLIFICATION. A laid-out `Fn` is emitted as a
// standalone shader FUNCTION, and the uniforms/varyings its body references are numbered against
// the build that first emitted it — reuse it in a second program and the body reads
// `nodeUniform2` where that program's `nodeUniform2` is a different type, producing a wall of
// "undeclared identifier" and "no matching overloaded function" at link time. This node carried
// `setLayout` from the day it was written and never rendered (nothing used `tint`), so the bug
// was latent; surf.maskedRGB found it the first time a factory node graph actually reached a GPU.
// Without a layout the Fn INLINES into the calling flow and every reference resolves per build.
const COLOR_NODE = Fn(() => materialColor.mul(kTint as unknown as typeof materialColor))();

// ── surf.maskedRGB: the UE `M_MaskedRGB` chain, ONE module-scope graph (docs/ai/tsl-patterns.ts
// patterns 2 + 3). Every varying part is an `ap*` material property, INCLUDING the two textures —
// `materialReference(name,'texture')` resolves to a TextureNode whose `.value` is re-read from the
// drawn material each frame, so a second colourway (or the face material later) is a new set of
// property values on the same program, never a second graph.
//
// The .d.ts types `materialReference` as its bare class, which carries none of the fluent
// swizzle/operator surface it actually has at runtime (the `kTint` cast above is the same
// problem, one node smaller). `nodeObject` is the runtime wrapper that gives it that surface;
// these three aliases give TypeScript the matching shape. Runtime-verified: see
// docs/ai/tsl-patterns.ts pattern 2 and the shots in tests/e2e/axonShots.e2e.ts.
type Vec4Node = ReturnType<typeof vec4>;
type Vec3Node = ReturnType<typeof vec3>;
type FloatNode = ReturnType<typeof float>;
const matRef = <T>(name: string, type: string): T =>
  nodeObject(materialReference(name, type)) as unknown as T;

const kMaskMap = matRef<Vec4Node>('apMaskMap', 'texture');
const kBaseMap = matRef<Vec4Node>('apBaseMap', 'texture');
const kColourR = matRef<Vec3Node>('apColourR', 'color');
const kColourG = matRef<Vec3Node>('apColourG', 'color');
const kColourB = matRef<Vec3Node>('apColourB', 'color');
const kBrightness = matRef<FloatNode>('apBrightness', 'float');
const kContrast = matRef<FloatNode>('apContrast', 'float');
const kSaturation = matRef<FloatNode>('apSaturation', 'float');

/**
 * VERIFIED against the UE asset, not inferred: `M_MaskedRGB.uasset`'s expression list is exactly
 * Add · Multiply · OneMinus · Power · Desaturation · `Blend_Overlay` over three VectorParameters
 * and three ScalarParameters, and the master's own defaults are Contrast 1.0 / Saturation 1.0
 * (so both are identity at 1, which is what pins their shape). Six of the eight shipped instances
 * sit on Contrast 1.0.
 *
 * ⚠️ WHAT IS AND IS NOT PROVEN. The region select (three masks × three colours, ADDED), the
 * overlay blend, and Desaturation-with-fraction-`1 − Saturation` are proven. The exact
 * Brightness/Contrast EXPRESSIONS are not — UE's Python API would not hand over the wiring, only
 * the node classes and the values — so they are written here as the two identity-at-1 forms those
 * classes admit (`OneMinus·Multiply·OneMinus` and `Power`). On `MI_Axon`'s own numbers the pair
 * lands the base detail at 0.895–1.0 either way, i.e. nearly flat, so the residual risk is small;
 * it is stated rather than hidden because a colourway with a low Brightness would expose it.
 *
 * ORDER MATTERS AND IT IS NOT THE OBVIOUS ONE. Brightness/Contrast act on the GREYSCALE BASE, not
 * on the final colour. `T_Axon_BaseColor` is a G8 map whose histogram is 0.90–1.0 — a faint wear
 * map that has to be expanded before it can modulate anything — and applying a ×5.2 brightness to
 * the finished colour instead blows the orange to white. That is what "port, don't bake" buys and
 * also what it costs: the chain has to be right, because there is no baked texture to fall back on.
 */
const MASKED_RGB_COLOR = Fn(() => {
  // Brightness first (identity at 1), then Contrast as a gamma over the result. `saturate` is
  // load-bearing: a large Brightness drives the darkest base texels negative and `pow()` of a
  // negative is NaN, which shows up as black speckle rather than as an error.
  const detail = saturate(oneMinus(oneMinus(kBaseMap.r).mul(kBrightness)));
  const base = pow(detail, kContrast);
  const tint = kColourR
    .mul(kMaskMap.r)
    .add(kColourG.mul(kMaskMap.g))
    .add(kColourB.mul(kMaskMap.b));
  // Engine `Blend_Overlay(Base, Blend)` with the TINT as Base and the detail as Blend.
  const lo = tint.mul(2).mul(base);
  const hi = oneMinus(oneMinus(tint).mul(2).mul(oneMinus(base)));
  // Componentwise select, written as a lerp because TSL's typed `mix` takes a SCALAR t only —
  // the overlay's branch is per channel (the tint is white in one region and near-black in another).
  const upper = step(vec3(0.5), tint);
  const blended = lo.add(hi.sub(lo).mul(upper));
  // UE's Desaturation node, with its own default luminance factors and Fraction = 1 − Saturation.
  const grey = dot(blended, vec3(0.3, 0.59, 0.11));
  return mix(vec3(grey), blended, kSaturation);
})(); // no `.setLayout()` — see the note on COLOR_NODE above; it breaks uniform numbering.

/** A stock node material that may carry the factory's `ap*` reference-resolved parameters. */
export type SurfaceMaterial = (MeshStandardNodeMaterial | MeshBasicNodeMaterial) & {
  apTint?: Color;
  apMaskMap?: Texture | null;
  apBaseMap?: Texture | null;
  apColourR?: Color;
  apColourG?: Color;
  apColourB?: Color;
  apBrightness?: number;
  apContrast?: number;
  apSaturation?: number;
};

/** {@link ColourValue} → Color. Tuples are written STRAIGHT into working space, unconverted. */
function toColour(v: ColourValue | undefined): Color {
  if (v === undefined) return new Color(0xffffff);
  // `setRGB` without a colorSpace argument writes the working (linear-sRGB) space directly, which
  // is what a dumped UE vector already is. `new Color(hex)` would decode from sRGB first.
  return Array.isArray(v) ? new Color().setRGB(v[0], v[1], v[2]) : new Color(v as number | string);
}

/**
 * Build one material instance for an archetype. The instance is a STOCK node material varying
 * only plain properties + texture slots; the only node it may carry is the shared module-scope
 * `COLOR_NODE` (when `tint` is used) — never a per-call graph.
 */
export function makeSurface(archetype: ArchetypeId, p: SurfaceParams = {}): SurfaceMaterial {
  const mat: SurfaceMaterial =
    archetype === 'surf.unlit' ? new MeshBasicNodeMaterial() : new MeshStandardNodeMaterial();
  mat.name = archetype;

  /*
   * ⚠️ `toColour`, NOT `new Color(...)`. This read `new Color(p.color)`, which decodes a hex from
   * sRGB — correct for an authored `'#8ab4f8'` and WRONG for a ported UE parameter, which is
   * already a linear vector. `MI_Martian_Tech_Paint_Blue`'s `Tint` is (0.412, 0.930, 1.0) linear;
   * the same number sent through the hex path lands at roughly (0.14, 0.85, 1.0) — a
   * plausible-looking blue that is not the artist's blue, on a whole colourway family.
   *
   * `toColour` already existed for exactly this reason (surf.maskedRGB's ported UE colours) and
   * already handles both spellings; `color` simply was not using it. The type widening to
   * `ColourValue` is what lets a level port hand over the linear triple UE dumped, unconverted.
   */
  if (p.color !== undefined) mat.color = toColour(p.color);
  if (p.side !== undefined) mat.side = p.side;

  const tex = p.textures ?? {};
  // surf.maskedRGB routes the base map through its OWN graph (`apBaseMap`); leaving it on the
  // stock `map` slot as well would bind a texture the shader never samples and put a
  // `!!material.map` difference into the render-object cache key for nothing.
  if (tex.map && archetype !== 'surf.maskedRGB') mat.map = tex.map;

  if (mat instanceof MeshStandardNodeMaterial) {
    // roughness/metalness default to 1.0 as MULTIPLIERS when an ORM map is bound (glTF
    // semantics); without one they are the literal surface values (metalness defaults 0).
    mat.roughness = p.roughness ?? 1.0;
    mat.metalness = p.metalness ?? (tex.orm ? 1.0 : 0.0);
    if (tex.normalMap) mat.normalMap = tex.normalMap;
    if (tex.orm) {
      // ONE texture, three slots — uuid dedupe collapses the GPU binding (rule 4).
      mat.aoMap = tex.orm;
      mat.roughnessMap = tex.orm;
      mat.metalnessMap = tex.orm;
    }
    if (archetype === 'surf.emissive') {
      mat.emissive = toColour(p.emissive ?? 0xffffff);
      mat.emissiveIntensity = p.emissiveIntensity ?? 1.0;
      if (tex.emissiveMap) mat.emissiveMap = tex.emissiveMap;
    } else if (p.emissive !== undefined) {
      mat.emissive = toColour(p.emissive);
      if (p.emissiveIntensity !== undefined) mat.emissiveIntensity = p.emissiveIntensity;
    }
  }

  if (archetype === 'surf.alphaTest') {
    // Non-zero by definition of the archetype (zero would silently fork back to surf.opaque's
    // program family — rule 1). Default matches glTF's alphaCutoff default.
    mat.alphaTest = p.alphaTest !== undefined && p.alphaTest > 0 ? p.alphaTest : 0.5;
  }

  if (archetype === 'surf.maskedRGB') {
    const m = p.maskedRGB ?? {};
    mat.colorNode = MASKED_RGB_COLOR; // the SHARED module-scope node — never a fresh graph
    mat.apMaskMap = tex.mask ?? null;
    mat.apBaseMap = tex.map ?? null;
    mat.apColourR = toColour(m.colourR);
    mat.apColourG = toColour(m.colourG);
    mat.apColourB = toColour(m.colourB);
    // The master's own defaults (dumped from M_MaskedRGB): 2 / 1 / 1.
    mat.apBrightness = m.brightness ?? 2;
    mat.apContrast = m.contrast ?? 1;
    mat.apSaturation = m.saturation ?? 1;
  } else if (p.tint !== undefined) {
    mat.colorNode = COLOR_NODE; // the SHARED module-scope node — never a fresh graph
    mat.apTint = new Color(p.tint);
  }

  return mat;
}
